// Stopgap scrubbing of customer personal data out of what this app sends to Sentry. Wired into
// `Sentry.init` in main.tsx.
//
// - The user: npm-p2g-app's SentryScope (src/providers/sentry.tsx) sets the customer's email
//   (the OIDC preferred_username) as both `user.email` and `user.username`. The id alone is enough
//   to find a customer's events, so everything else is dropped. The proper fix is in the package.
// - Breadcrumbs: fetch/navigation URLs carry postcodes and address searches in the query string
//   (e.g. the tracking lookup in useTracking.ts), and analytics / console breadcrumbs carry raw
//   name and email (the package's `login` event before 3.14.0, and the purchase payload built in
//   useRecordPurchaseAnalytics.ts).

const FILTERED = '[Filtered]';

/**
 * Query parameters whose value is customer data (postcodes, address / free-text searches, email) or
 * a capability (`hash` on a payment link). Matched on the exact name, case-insensitively.
 */
const SENSITIVE_QUERY_PARAMETERS = ['postcode', 'query', 'search', 'searchTerm', 'lastSearchTerm', 'email', 'hash'];

const SENSITIVE_QUERY_PATTERN = new RegExp(`([?&])(${SENSITIVE_QUERY_PARAMETERS.join('|')})=[^&#]*`, 'gi');

/**
 * Object keys whose value is a customer's name or email: the package's raw identity keys
 * (RAW_IDENTITY_KEYS - email, forename, surname) and the purchase payload's name/email fields.
 * Compared lower-cased.
 */
const SENSITIVE_KEYS = new Set(['email', 'forename', 'surname', 'user_email', 'user_name', 'useremail', 'username']);

/** Deep enough for `console` breadcrumb arguments -> payload -> ecommerce -> field. */
const MAX_DEPTH = 6;

type ScrubbableUser = { id?: string | number } & Record<string, unknown>;

/**
 * Keeps only `user.id`. Removes the user entirely when there is no id, so no email-only user is
 * ever sent.
 */
export function scrubEventUser<T extends { user?: ScrubbableUser }>(event: T): T {
  if (!event.user) {
    return event;
  }

  const { user, ...rest } = event;
  if (user.id === undefined || user.id === null || user.id === '') {
    return rest as T;
  }

  return { ...rest, user: { id: user.id } } as T;
}

function scrubUrlQuery(value: string) {
  return value.replace(SENSITIVE_QUERY_PATTERN, `$1$2=${FILTERED}`);
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') {
    return scrubUrlQuery(value);
  }

  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEYS.has(key.toLowerCase()) && item !== undefined && item !== null
        ? FILTERED
        : scrubValue(item, depth + 1)
    ])
  );
}

type ScrubbableBreadcrumb = { message?: string; data?: Record<string, unknown> };

/**
 * `beforeBreadcrumb`: filters sensitive query parameters out of every string in the breadcrumb's
 * message and data (fetch/xhr `url`, navigation `from`/`to`, console arguments) and replaces the
 * value of any name/email key in its data. Returns a copy; never drops the breadcrumb.
 */
export function scrubBreadcrumb<T extends ScrubbableBreadcrumb>(breadcrumb: T): T | null {
  if (!breadcrumb.data && typeof breadcrumb.message !== 'string') {
    return breadcrumb;
  }

  return {
    ...breadcrumb,
    ...(typeof breadcrumb.message === 'string' ? { message: scrubUrlQuery(breadcrumb.message) } : {}),
    ...(breadcrumb.data ? { data: scrubValue(breadcrumb.data, 0) as Record<string, unknown> } : {})
  };
}
